/**
 * Decides when a deploy may reload the tab.
 *
 * A reload while someone is typing, or while a dialog is open, throws away
 * the text they have not saved yet. The version check and the stale-chunk
 * recovery both ask this module before calling location.reload.
 */

export const INPUT_IDLE_MS = 2 * 60 * 1000
export const VERSION_UPDATE_EVENT = 'fsj-version-update'
export const DIALOG_SELECTOR = '.v-dialog.v-overlay--active'

const STALE_SIGNATURES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'is not a valid JavaScript MIME type',
  'Unable to preload CSS',
]

const IGNORED_INPUT_TYPES = new Set([
  'search', 'checkbox', 'radio', 'button', 'submit', 'reset',
  'file', 'hidden', 'range', 'color',
])

let lastEditAt = 0
let listenersInstalled = false

export function resetReloadGuardState() {
  lastEditAt = 0
  listenersInstalled = false
}

function isElement(el) {
  return !!el && el.nodeType === 1
}

export function isTextEntryTarget(el) {
  if (!isElement(el)) return false
  if (el.closest?.('[data-fsj-reload-safe]')) return false
  if (el.readOnly || el.disabled) return false
  const tag = el.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (el.isContentEditable) return true
  if (tag === 'INPUT') {
    const type = String(el.getAttribute?.('type') || 'text').toLowerCase()
    return !IGNORED_INPUT_TYPES.has(type)
  }
  return false
}

export function isLongFormTarget(el) {
  if (!isTextEntryTarget(el)) return false
  return el.tagName === 'TEXTAREA' || !!el.isContentEditable
}

export function noteUserEdit(el, now = Date.now()) {
  if (!isTextEntryTarget(el)) return false
  lastEditAt = now
  return true
}

export function recentEdit(now = Date.now(), idleMs = INPUT_IDLE_MS) {
  return lastEditAt > 0 && (now - lastEditAt) < idleMs
}

export function dialogOpen(doc) {
  if (!doc) return false
  if (doc.documentElement?.dataset?.annotationActive === '1') return true
  try {
    return !!doc.querySelector?.(DIALOG_SELECTOR)
  } catch {
    return false
  }
}

/**
 * @returns {''|'call'|'form'|'dialog'|'focus'|'typing'}
 */
export function reloadBlockReason({
  now = Date.now(),
  win = typeof window !== 'undefined' ? window : {},
  doc = typeof document !== 'undefined' ? document : null,
  activeElement,
} = {}) {
  if (win.__dialerCallActive === true) return 'call'
  if (win.__formDirty === true) return 'form'
  const root = doc
  if (dialogOpen(root)) return 'dialog'
  const active = activeElement !== undefined ? activeElement : root?.activeElement
  if (isLongFormTarget(active)) return 'focus'
  if (recentEdit(now)) return 'typing'
  return ''
}

export function installReloadGuardListeners(doc = typeof document !== 'undefined' ? document : null) {
  if (listenersInstalled || !doc?.addEventListener) return
  listenersInstalled = true
  const onEdit = (event) => noteUserEdit(event?.target)
  doc.addEventListener('input', onEdit, true)
  doc.addEventListener('change', onEdit, true)
}

export function publishVersionUpdate(detail) {
  const payload = detail || { pending: false }
  try {
    window.__fsjVersionUpdate = payload
    window.dispatchEvent(new CustomEvent(VERSION_UPDATE_EVENT, { detail: payload }))
  } catch { /* no window in tests */ }
}

export function decideVersionReload({
  localVersion,
  serverVersion,
  confirmVersion,
  blockedReason = '',
  attempt = 1,
  maxAttempts = 2,
  recentReloads = 0,
  maxReloads = 3,
  disabled = false,
  reloading = false,
} = {}) {
  if (reloading || disabled) return { action: 'skip', why: 'disabled' }
  if (!serverVersion || serverVersion === localVersion) return { action: 'current' }
  if (confirmVersion !== serverVersion) return { action: 'skip', why: 'flapping' }
  if (attempt > maxAttempts) return { action: 'skip', why: 'max-attempts' }
  if (recentReloads >= maxReloads) return { action: 'breaker' }
  if (blockedReason) return { action: 'postpone', reason: blockedReason, banner: true }
  return { action: 'reload' }
}

export function decideStaleChunkReload({
  message = '',
  blockedReason = '',
  count = 0,
  lastAt = 0,
  now = Date.now(),
  windowMs = 60000,
  max = 2,
} = {}) {
  if (!STALE_SIGNATURES.some(sig => String(message).includes(sig))) return { action: 'ignore' }
  if (blockedReason) return { action: 'postpone', reason: blockedReason, banner: true }
  if (count >= max) return { action: 'stop' }
  if (!lastAt || (now - lastAt) > windowMs) return { action: 'reload' }
  return { action: 'stop' }
}
